import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getStrategyRadar } from './api'
import {
  loadStrategyRadar,
  type RadarBStockUniverse,
  type RadarListingBatch,
  type RadarListingResult,
  type RadarMarketBatch,
  type RadarMarketResult,
  type StrategyRadarSnapshot
} from './strategyRadarApi'
import { fetchPublicStrategyRadar } from './strategyRadarPublicApi'
import {
  planAnnouncementNotifications,
  planListingMilestoneNotifications
} from './strategyRadarNotifications'
import './strategyRadar.css'
import {
  MAINSTREAM_MARKETS,
  STOCK_MAPPINGS,
  btcRangeZone,
  formatCountdown,
  listingPhase,
  type RadarTone,
  type SwingSignal
} from './strategyRadarModel'
import type { StrategyRadarError, StrategyRadarSectionStatus } from './types'

const notificationPreferenceKey = 'pulse.strategyRadar.notifications'
const seenAnnouncementsKey = 'pulse.strategyRadar.seenAnnouncements'
const seenListingMilestonesKey = 'pulse.strategyRadar.seenListingMilestones'
let seenAnnouncementsMemory: string[] | null = null
let seenListingMilestonesMemory: string[] = []

type StrategyRadarProps = {
  onOpenMarket: (symbol: string, timeframe: '1m' | '4h') => void
  chartTarget: 'internal' | 'binance'
  backendEnabled: boolean
}

type RadarMetadata = Pick<
  StrategyRadarSnapshot,
  'methodologyVersion' | 'generatedAt' | 'venue' | 'marketType' | 'timeframe' | 'researchOnly' | 'isTradeablePrice'
>

export function StrategyRadar({ onOpenMarket, chartTarget, backendEnabled }: StrategyRadarProps) {
  const [marketBatch, setMarketBatch] = useState<RadarMarketBatch | null>(null)
  const [listingBatch, setListingBatch] = useState<RadarListingBatch | null>(null)
  const [bStockUniverse, setBStockUniverse] = useState<RadarBStockUniverse | null>(null)
  const [metadata, setMetadata] = useState<RadarMetadata | null>(null)
  const [marketError, setMarketError] = useState('')
  const [listingError, setListingError] = useState('')
  const [aggregateError, setAggregateError] = useState('')
  const [notificationError, setNotificationError] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [clock, setClock] = useState(() => Date.now())
  const [notificationStatus, setNotificationStatus] = useState(() => initialNotificationStatus())
  const notificationEnabledRef = useRef(notificationStatus === 'enabled')
  const controllerRef = useRef<AbortController | null>(null)
  const refreshInFlightRef = useRef(false)
  const refreshSequenceRef = useRef(0)

  const refresh = useCallback(async (manual = false) => {
    const replaceInFlight = manual
    if (refreshInFlightRef.current && !replaceInFlight) return
    if (replaceInFlight) controllerRef.current?.abort()
    const sequence = refreshSequenceRef.current + 1
    refreshSequenceRef.current = sequence
    refreshInFlightRef.current = true
    const controller = new AbortController()
    controllerRef.current = controller
    setRefreshing(true)
    setAggregateError('')
    const isCurrent = () => refreshSequenceRef.current === sequence && !controller.signal.aborted
    try {
      const value = await loadStrategyRadar(
        backendEnabled,
        manual,
        getStrategyRadar,
        controller.signal,
        fetchPublicStrategyRadar
      )
      if (!value || !isCurrent()) return
      setMetadata({
        methodologyVersion: value.methodologyVersion,
        generatedAt: value.generatedAt,
        venue: value.venue,
        marketType: value.marketType,
        timeframe: value.timeframe,
        researchOnly: value.researchOnly,
        isTradeablePrice: value.isTradeablePrice
      })
      setMarketBatch(value.markets)
      setListingBatch(value.listings)
      setBStockUniverse(value.bStockUniverse)
      setMarketError(sectionError(value.markets.status, value.markets.errors, value.markets.items.length, '聚合行情暂无可用标的'))
      setListingError(sectionError(value.listings.status, value.listings.errors, value.listings.items.length, '公告分区暂无可用条目'))
      setAggregateError(value.errors[0]?.message ?? '')

      if (value.listings.status === 'available' || value.listings.status === 'partial') {
        const notificationItems = value.listings.items.filter(item => item.status === 'available')
        const announcementFailed = processAnnouncementNotifications(notificationItems, notificationEnabledRef.current)
        const milestoneFailed = processListingMilestoneNotifications(notificationItems, notificationEnabledRef.current)
        setNotificationError(announcementFailed || milestoneFailed ? '浏览器拒绝创建系统通知；页面内雷达继续运行。' : '')
      }
    } catch (error) {
      if (isCurrent()) {
        const message = errorMessage(error)
        setAggregateError(message)
        setMarketBatch(previous => failClosedMarketBatch(previous, message))
        setListingBatch(previous => failClosedListingBatch(previous, message))
        setBStockUniverse(previous => failClosedBStockUniverse(previous, message))
      }
    } finally {
      if (refreshSequenceRef.current === sequence) {
        setClock(Date.now())
        setRefreshing(false)
        refreshInFlightRef.current = false
      }
    }
  }, [backendEnabled])

  useEffect(() => {
    void refresh()
    const refreshWhenVisible = () => {
      if (!document.hidden) void refresh()
    }
    const refreshTimer = window.setInterval(refreshWhenVisible, 60_000)
    const clockTimer = window.setInterval(() => setClock(Date.now()), 1_000)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.clearInterval(refreshTimer)
      window.clearInterval(clockTimer)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
      controllerRef.current?.abort()
      refreshSequenceRef.current += 1
      refreshInFlightRef.current = false
    }
  }, [backendEnabled, refresh])

  const announcements = useMemo(() => listingBatch?.items ?? [], [listingBatch])

  const marketBySymbol = useMemo(
    () => new Map((marketBatch?.items ?? []).map(item => [item.symbol, item])),
    [marketBatch]
  )
  const bStockRows = useMemo(() => {
    const universeBySymbol = new Map((bStockUniverse?.items ?? []).map(item => [item.symbol, item]))
    const registryBySymbol = new Map<string, (typeof STOCK_MAPPINGS)[number]>(
      STOCK_MAPPINGS.map(item => [item.symbol, item])
    )
    const majorSymbols = new Set(MAINSTREAM_MARKETS.map(item => item.symbol as string))
    return (marketBatch?.items ?? [])
      .filter(item => !majorSymbols.has(item.symbol))
      .map(item => {
        const discovered = universeBySymbol.get(item.symbol)
        if (discovered) return discovered
        const registered = registryBySymbol.get(item.symbol)
        return registered ? {
          symbol: registered.symbol,
          token: registered.token,
          fullName: `${registered.company} (bStocks)`,
          company: registered.company,
          underlying: registered.underlying,
          theme: registered.theme,
          volume: 0,
          registryStatus: 'registered' as const
        } : {
          symbol: item.symbol,
          token: cleanSymbol(item.symbol),
          fullName: 'Binance official bStock',
          company: 'Official discovered',
          underlying: null,
          theme: null,
          volume: 0,
          registryStatus: 'official-discovered' as const
        }
      })
  }, [bStockUniverse, marketBatch])
  const listingRows = useMemo(() => rankAnnouncements(announcements, clock).slice(0, 5), [announcements, clock])
  const btcMarket = marketBySymbol.get('BTCUSDT')
  const btcSignal = marketSignalForDisplay(btcMarket)
  const btcDisplayPrice = btcSignal.state === 'unavailable' ? null : (btcSignal.latestPrice ?? btcSignal.price)
  const calculatedBtcZone = btcRangeZone(btcDisplayPrice)
  const btcZone = {
    ...calculatedBtcZone,
    id: btcMarket?.btcZone ?? calculatedBtcZone.id,
    label: btcMarket?.btcZoneLabel ?? calculatedBtcZone.label,
    action: btcMarket?.btcZoneAction ?? calculatedBtcZone.action
  }
  const actionableCount = sectionCanBeActionable(marketBatch?.status)
    ? (marketBatch?.items ?? []).filter(item => marketItemCanBeActionable(item) && item.signal.state === 'buy-watch').length
    : 0
  const nextListing = listingRows.find(item => (item.tradingAt ?? 0) > clock)
  const nextListingPhase = nextListing ? listingPhase(nextListing.tradingAt, clock) : null
  const lastUpdatedAt = latestRadarTimestamp(metadata, marketBatch, listingBatch, bStockUniverse)
  const radarHealthTone = combinedSectionTone(marketBatch?.status, listingBatch?.status)

  async function toggleNotifications() {
    if (notificationStatus === 'enabled') {
      notificationEnabledRef.current = false
      setNotificationStatus('disabled')
      setNotificationError('')
      writeLocal(notificationPreferenceKey, 'false')
      return
    }
    if (typeof Notification === 'undefined') {
      setNotificationStatus('unsupported')
      return
    }
    let permission: NotificationPermission
    try {
      permission = await Notification.requestPermission()
    } catch (error) {
      notificationEnabledRef.current = false
      setNotificationStatus('disabled')
      setNotificationError(`通知权限请求失败：${errorMessage(error)}`)
      return
    }
    if (permission !== 'granted') {
      notificationEnabledRef.current = false
      setNotificationStatus('blocked')
      writeLocal(notificationPreferenceKey, 'false')
      return
    }
    notificationEnabledRef.current = true
    setNotificationStatus('enabled')
    writeLocal(notificationPreferenceKey, 'true')
    const delivered = showNotification('脉冲策略雷达已开启', {
      body: `页面可见时每 60 秒读取${backendEnabled ? '服务端聚合快照' : '币安公开数据'}，并检查 T−15 / T+0 / T+3 提醒。`
    })
    const milestoneFailed = processListingMilestoneNotifications(announcements, true)
    setNotificationError(!delivered || milestoneFailed ? '浏览器拒绝创建系统通知；页面内雷达继续运行。' : '')
  }

  return <section className="page padded strategy-radar">
    <header className="trader-toolbar">
      <div className="trader-brand">
        <small>PULSE / BINANCE SPOT</small>
        <h1>STRATEGY RADAR</h1>
      </div>

      <div className="trader-kpis" aria-label="策略概览">
        <TraderKpi
          label="COVERAGE"
          value={(marketBatch?.availableCount ?? 0) + '/' + (marketBatch?.requestedCount ?? '—')}
          detail={sectionStatusLabel(marketBatch?.status)}
          tone={sectionTone(marketBatch?.status)}
        />
        <TraderKpi
          label="BUY WATCH"
          value={String(actionableCount)}
          detail="CLOSED 4H"
          tone={actionableCount ? 'positive' : 'neutral'}
        />
        <TraderKpi
          label="BSTOCKS"
          value={`${bStockUniverse?.selectedCount ?? bStockRows.length}/${bStockUniverse?.discoveredCount || '—'}`}
          detail={bStockUniverse?.truncated ? `TOP ${bStockUniverse.limit}` : bStockUniverse?.status?.toUpperCase() ?? 'LOADING'}
          tone={bStockUniverseTone(bStockUniverse?.status)}
        />
        <TraderKpi
          label="NEXT LISTING"
          value={nextListingPhase ? formatCountdown(nextListingPhase.countdownMs) : '—'}
          detail={nextListing?.symbols.slice(0, 2).map(cleanSymbol).join(' / ') || 'NO SCHEDULE'}
          tone={nextListingPhase?.tone ?? 'neutral'}
        />
        <TraderKpi
          label="BTC ZONE"
          value={btcDisplayPrice ? formatCompactPrice(btcDisplayPrice) : '—'}
          detail={btcZone.label}
          tone={btcZone.tone}
        />
      </div>

      <div className="trader-actions" aria-label="策略操作">
        <button
          type="button"
          className={notificationStatus === 'enabled' ? 'trader-action enabled' : 'trader-action'}
          onClick={() => void toggleNotifications()}
          aria-label={notificationButtonLabel(notificationStatus)}
          title={notificationStatus === 'enabled' ? '页面可见时每 60 秒检查 T−15 / T0 / T+3 提醒' : notificationButtonLabel(notificationStatus)}
        >
          <span>ALERT</span>
          <b>{notificationCompactLabel(notificationStatus)}</b>
        </button>
        <button
          type="button"
          className="trader-action"
          disabled={refreshing}
          onClick={() => void refresh(true)}
          aria-label={refreshing ? '正在刷新策略数据' : '刷新策略数据'}
          title={lastUpdatedAt ? '上次更新 ' + formatShanghai(lastUpdatedAt, true) : '等待首次数据'}
        >
          <span>REFRESH</span>
          <b>{refreshing ? 'SYNC' : '↻'}</b>
        </button>
      </div>
    </header>

    <div className="trader-statusline" aria-label="数据状态" role="status">
      <span className={`feed-state ${radarHealthTone}`}><i />{backendEnabled ? 'SERVER AGGREGATED' : 'BINANCE PUBLIC'}</span>
      <span>SPOT 1×</span>
      <span>4H CLOSED</span>
      <span>LISTING 1M CLOSED</span>
      <span className={sectionTone(marketBatch?.status)}>MARKET {sectionStatusLabel(marketBatch?.status)}</span>
      <span className={sectionTone(listingBatch?.status)}>LISTING {sectionStatusLabel(listingBatch?.status)}</span>
      <span className={bStockUniverseTone(bStockUniverse?.status)}>BSTOCK {bStockUniverse?.status?.toUpperCase() ?? 'LOADING'}</span>
      <span>{lastUpdatedAt ? formatShanghai(lastUpdatedAt, true) : 'WAITING DATA'}</span>
    </div>

    {(aggregateError || marketError || listingError || bStockUniverse?.warning || notificationError || notificationStatus === 'blocked' || notificationStatus === 'unsupported') && <div className={`radar-notices${aggregateError || marketError || listingError || notificationError || bStockUniverse?.status === 'unavailable' ? ' has-error' : ''}`} role={aggregateError || marketError || listingError || notificationError || bStockUniverse?.status === 'unavailable' ? 'alert' : 'status'}>
      {aggregateError && <span><b>AGG</b>{aggregateError}</span>}
      {marketError && <span><b>MKT</b>{marketError}</span>}
      {listingError && <span><b>NEWS</b>{listingError}</span>}
      {bStockUniverse?.warning && <span><b>BSTOCK</b>{bStockUniverse.warning}</span>}
      {notificationError && <span><b>ALERT</b>{notificationError}</span>}
      {notificationStatus === 'blocked' && <span><b>ALERT</b>通知权限被拒绝；仅页面内监控。</span>}
      {notificationStatus === 'unsupported' && <span><b>ALERT</b>当前浏览器不支持页面通知。</span>}
    </div>}

    <div className="trader-top-grid">
      <section className="trader-panel listing-panel" id="listing-radar">
        <header className="panel-heading">
          <div><small>01 / ULTRA SHORT</small><h2>NEW LISTING TAPE</h2></div>
          <span>{announcements.length ? announcements.length + ' LEADS' : refreshing ? 'SYNCING' : 'NO QUALIFIED LEAD'} · {sectionStatusLabel(listingBatch?.status)}</span>
        </header>

        <div className="timing-strip" aria-label="上新候选时间窗">
          <span><b>T−15</b>WATCH / OPEN 1M</span>
          <span className="negative"><b>T0–3</b>NO CHASE</span>
          <span className="positive"><b>T+3–15</b>1M CONFIRM</span>
          <span className="warning"><b>T+15</b>WINDOW END</span>
        </div>

        <div className="trader-table-wrap listing-table-wrap">
          <table className="trader-table listing-table">
            <thead><tr><th>TYPE</th><th>OPEN (SH)</th><th>ASSET</th><th>PHASE</th><th>COUNTDOWN</th><th>1M CHECK</th><th>TRIGGER / RISK</th><th /></tr></thead>
            <tbody>
              {listingRows.map(item => <CompactListingRow key={item.code} item={item} now={clock} onOpenMarket={onOpenMarket} chartTarget={chartTarget} />)}
              {!listingRows.length && <tr className="empty-row"><td colSpan={8}>{refreshing ? 'SYNCING OFFICIAL LISTING FEED…' : 'NO QUALIFIED LISTING LEAD / SCHEDULE'}</td></tr>}
            </tbody>
          </table>
        </div>

        <footer className="panel-foot">
          <span>LEAD ≠ SPOT SCHEDULE / T+3–15 REQUIRES CLOSED 1M PULLBACK + RECLAIM + LIQUIDITY</span>
          <b>EXIT MANUAL</b>
        </footer>
      </section>

      <section className="trader-panel btc-panel" id="btc-range">
        <header className="panel-heading btc-panel-heading">
          <div><small>03 / MAJOR PLAYBOOK</small><h2>BTCUSDT · 60–70K</h2></div>
          <div className="btc-live-quote">
            <b>{formatOptionalPrice(btcSignal.latestPrice ?? btcDisplayPrice)}</b>
            {btcSignal.latestPrice != null && btcSignal.price != null && (
              <span className={numberClass(btcSignal.latestPrice - btcSignal.price)}>
                {btcSignal.latestPrice > btcSignal.price ? '+' : ''}{formatPrice(btcSignal.latestPrice - btcSignal.price)}
              </span>
            )}
            <span className={numberClass(btcMarket?.change24hPct)}>{formatChange(btcMarket?.change24hPct)}</span>
            <span className={'signal-pill ' + btcZone.tone}>{btcZone.label}</span>
            {btcSignal.latestPriceAsOf != null && (
              <small className="latest-asof">{formatShanghai(btcSignal.latestPriceAsOf)}</small>
            )}
          </div>
        </header>

        <div className="btc-statline">
          <span><small>SIGNAL</small><b className={btcSignal.tone}>{btcSignal.label}</b></span>
          <span title={btcSignal.forecastSummary}><small>NEXT 1–3×4H</small><b className={forecastTone(btcSignal.trendForecast)}>{forecastLabel(btcSignal.trendForecast)}</b></span>
          <span><small>CONF</small><b>{confidenceLabel(btcSignal.forecastConfidence)}</b></span>
          {btcSignal.latestPrice != null && (
            <span title={`1M CLOSE · ${btcSignal.latestPriceAsOf ? formatShanghai(btcSignal.latestPriceAsOf) : '待核验'}`}>
              <small>1M CLOSE</small>
              <b>{formatOptionalPrice(btcSignal.latestPrice)}</b>
            </span>
          )}
          <span><small>EMA20</small><b>{formatOptionalPrice(btcSignal.ema20)}</b></span>
          <span><small>EMA50</small><b>{formatOptionalPrice(btcSignal.ema50)}</b></span>
          <span><small>RSI14</small><b>{btcSignal.rsi14?.toFixed(1) ?? '—'}</b></span>
        </div>

        <div className="btc-range compact" aria-label={'BTC 区间位置 ' + btcZone.positionPct.toFixed(1) + '%'}>
          <div className="btc-range-labels"><span>60K</span><span>62K</span><span>68K</span><span>70K</span></div>
          <div className="btc-range-track">
            <i className="zone accumulate" />
            <i className="zone observe" />
            <i className="zone reduce" />
            {btcDisplayPrice != null && <b style={{ left: btcZone.positionPct + '%' }}><span>{formatCompactPrice(btcDisplayPrice)}</span></b>}
          </div>
          <div className="btc-zone-row">
            <span><b>60–62K</b>止跌 / 小额分批</span>
            <span><b>62–68K</b>等 EMA20 回踩</span>
            <span><b>68–70K</b>禁追 / 管理风险</span>
          </div>
        </div>

        <div className="btc-execution-row">
          <span title={btcZone.action}><b>ZONE ACTION</b>{btcZone.action}</span>
          <span title={`${btcSignal.entryRule} · ${btcSignal.positionGuidance}`}><b>ENTRY / POSITION</b>{btcSignal.entryRule} · {btcSignal.positionGuidance}</span>
          <span title={btcSignal.exitRule}><b>EXIT / INVALID</b>{btcSignal.exitRule}</span>
          <button type="button" onClick={() => onOpenMarket('BTCUSDT', '4h')}>{chartTarget === 'binance' ? 'BINANCE ↗' : 'BTC 4H →'}</button>
        </div>
      </section>
    </div>

    <section className="trader-panel matrix-panel" id="market-radar">
      <header className="panel-heading">
        <div><small>02–03 / SWING MATRIX</small><h2>MARKET SIGNAL MATRIX</h2></div>
        <span>BSTOCKS {bStockRows.length}/{bStockUniverse?.discoveredCount || '—'} · MAJORS 3 · NEXT 1–3×4H · CLOSED ONLY</span>
      </header>

      <div className="trader-table-wrap matrix-table-wrap">
        <table className="trader-table market-matrix">
          <thead>
            <tr><th>GROUP</th><th>SYMBOL</th><th>LAST</th><th>24H</th><th>SIGNAL</th><th>EMA20</th><th>EMA50</th><th>RSI</th><th>FORECAST</th><th>CONF</th><th>POS</th><th>ENTRY</th><th>EXIT / INVALID</th><th>DATA</th><th /></tr>
          </thead>
          <tbody>
            {bStockRows.map(mapping => <MarketMatrixRow
              key={mapping.symbol}
              group="BSTOCK"
              symbol={mapping.symbol}
              asset={mapping.token}
              descriptor={[mapping.underlying, mapping.company].filter(Boolean).join(' · ')}
              market={marketBySymbol.get(mapping.symbol)}
              onOpenMarket={onOpenMarket}
              chartTarget={chartTarget}
            />)}
            {MAINSTREAM_MARKETS.map(item => <MarketMatrixRow
              key={item.symbol}
              group="MAJOR"
              symbol={item.symbol}
              asset={item.asset}
              descriptor={item.name}
              market={marketBySymbol.get(item.symbol)}
              onOpenMarket={onOpenMarket}
              chartTarget={chartTarget}
            />)}
          </tbody>
        </table>
      </div>
    </section>

  </section>
}

function TraderKpi({ label, value, detail, tone }: { label: string; value: string; detail: string; tone: RadarTone }) {
  return <div className={'trader-kpi ' + tone}><small>{label}</small><b>{value}</b><span>{detail}</span></div>
}

function CompactListingRow({ item, now, onOpenMarket, chartTarget }: {
  item: RadarListingResult
  now: number
  onOpenMarket: StrategyRadarProps['onOpenMarket']
  chartTarget: StrategyRadarProps['chartTarget']
}) {
  const phase = listingPhase(item.tradingAt, now)
  const firstSymbol = item.symbols.find(symbol => symbol.endsWith('USDT'))
  const chartTimeframe = phase.state === 'opening' || phase.state === 'entry-window' ? '1m' : '4h'
  const triggerParts = [item.warning, phase.action]
  if (phase.state === 'entry-window') triggerParts.push(item.entryConfirmation.summary)
  const trigger = triggerParts.filter(Boolean).join(' · ')
  const rowTone = item.entryConfirmation.state === 'confirmed' ? item.entryConfirmation.tone : phase.tone
  return <tr className={'listing-tape-row ' + rowTone}>
    <td><span className={'group-chip listing-kind ' + listingKindClass(item.kind)}>{item.kindLabel}</span></td>
    <td className="listing-open"><b>{item.tradingAt ? formatShanghai(item.tradingAt) : '—'}</b><small>{item.tradingAt ? 'PLANNED SH' : 'LEAD / NO CLOCK'}</small></td>
    <td className="listing-assets">{item.symbols.slice(0, 4).map(cleanSymbol).join(' / ') || '—'}</td>
    <td><span className={'signal-pill ' + phase.tone}>{phase.label}</span></td>
    <td className="mono-cell">{item.tradingAt ? formatCountdown(phase.countdownMs) : '—'}</td>
    <td title={item.entryConfirmation.summary}><span className={'signal-pill ' + item.entryConfirmation.tone}>{item.entryConfirmation.label}</span></td>
    <td className="rule-cell" title={trigger}><span>{trigger}</span></td>
    <td className="row-actions">
      <a href={item.articleUrl} target="_blank" rel="noreferrer" title="币安官方公告">DOC ↗</a>
      {firstSymbol && <button type="button" onClick={() => onOpenMarket(firstSymbol, chartTimeframe)}>{chartTarget === 'binance' ? 'BINANCE ↗' : chartTimeframe.toUpperCase() + ' →'}</button>}
    </td>
  </tr>
}

function MarketMatrixRow({ group, symbol, asset, descriptor, market, onOpenMarket, chartTarget }: {
  group: 'BSTOCK' | 'MAJOR'
  symbol: string
  asset: string
  descriptor: string
  market: RadarMarketResult | undefined
  onOpenMarket: StrategyRadarProps['onOpenMarket']
  chartTarget: StrategyRadarProps['chartTarget']
}) {
  const signal = marketSignalForDisplay(market)
  const detail = marketStatusDetail(market, signal)
  return <tr className={'market-row ' + signal.tone}>
    <td><span className={'group-chip ' + group.toLowerCase()}>{group}</span></td>
    <td className="matrix-symbol"><b>{asset}</b><small>{descriptor || 'BINANCE OFFICIAL'}</small></td>
    <td className="price-cell">
      <b>{formatOptionalPrice(signal.latestPrice ?? signal.price)}</b>
      {signal.latestPrice != null && signal.price != null && signal.latestPrice !== signal.price && (
        <small className={numberClass(signal.latestPrice - signal.price)}>
          {signal.latestPrice > signal.price ? '+' : ''}{formatPrice(signal.latestPrice - signal.price)} · 1M
        </small>
      )}
    </td>
    <td className={numberClass(market?.change24hPct)}>{formatChange(market?.change24hPct)}</td>
    <td><span className={'signal-pill ' + signal.tone}>{signal.label}</span></td>
    <td className="mono-cell">{formatOptionalPrice(signal.ema20)}</td>
    <td className="mono-cell">{formatOptionalPrice(signal.ema50)}</td>
    <td className="mono-cell">{signal.rsi14?.toFixed(1) ?? '—'}</td>
    <td className={'bias-cell ' + forecastTone(signal.trendForecast)} title={signal.forecastSummary}>{forecastLabel(signal.trendForecast)}</td>
    <td className="mono-cell" title="规则一致度，不是统计胜率">{confidenceLabel(signal.forecastConfidence)}</td>
    <td className={'position-cell ' + positionTone(signal.positionTier)} title={signal.positionGuidance}>{positionLabel(signal.positionTier)}</td>
    <td className="rule-cell" title={signal.entryRule}><span>{signal.entryRule}</span></td>
    <td className="rule-cell" title={signal.exitRule}><span>{signal.exitRule}</span></td>
    <td className="data-cell" title={detail}>
      <b>{marketDataState(market)}</b>
      <small>{marketDataTrace(market, signal)}</small>
    </td>
    <td className="row-actions"><button type="button" onClick={() => onOpenMarket(symbol, '4h')}>{chartTarget === 'binance' ? '↗' : '4H'}</button></td>
  </tr>
}

function rankAnnouncements(items: RadarListingResult[], now: number) {
  return [...items].sort((left, right) => {
    const leftFuture = left.tradingAt != null && left.tradingAt > now
    const rightFuture = right.tradingAt != null && right.tradingAt > now
    if (leftFuture !== rightFuture) return leftFuture ? -1 : 1
    if (leftFuture && rightFuture) return (left.tradingAt ?? 0) - (right.tradingAt ?? 0)
    return (right.tradingAt ?? right.releaseAt) - (left.tradingAt ?? left.releaseAt)
  })
}

function processAnnouncementNotifications(items: RadarListingResult[], enabled: boolean) {
  const stored = readSeenAnnouncements()
  if (stored == null) {
    writeSeenAnnouncements(items.map(item => item.code))
    return false
  }
  const seen = new Set(stored)
  let deliveryFailed = false
  if (enabled && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    planAnnouncementNotifications(items, seen).forEach(plan => {
      if (!showNotification(plan.title, { body: plan.body })) deliveryFailed = true
    })
  }
  writeSeenAnnouncements([...new Set([...stored, ...items.map(item => item.code)])].slice(-100))
  return deliveryFailed
}

function processListingMilestoneNotifications(items: RadarListingResult[], enabled: boolean, now = Date.now()) {
  if (!enabled || typeof Notification === 'undefined' || Notification.permission !== 'granted') return false
  const seen = new Set(readStringList(seenListingMilestonesKey, seenListingMilestonesMemory))
  let deliveryFailed = false
  planListingMilestoneNotifications(items, seen, now).forEach(plan => {
    const delivered = showNotification(plan.title, { body: plan.body })
    if (delivered) seen.add(plan.id)
    else deliveryFailed = true
  })
  seenListingMilestonesMemory = [...seen].slice(-200)
  writeStringList(seenListingMilestonesKey, seenListingMilestonesMemory)
  return deliveryFailed
}

function readSeenAnnouncements(): string[] | null {
  try {
    const raw = window.localStorage.getItem(seenAnnouncementsKey)
    if (raw == null) return seenAnnouncementsMemory
    const parsed = JSON.parse(raw)
    const codes = Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
    seenAnnouncementsMemory = codes
    return codes
  } catch {
    return seenAnnouncementsMemory
  }
}

function writeSeenAnnouncements(codes: string[]) {
  seenAnnouncementsMemory = codes
  try {
    window.localStorage.setItem(seenAnnouncementsKey, JSON.stringify(codes))
  } catch {
    // Browser privacy mode may reject local persistence; the live page still works.
  }
}

function readStringList(key: string, fallback: string[]) {
  try {
    const raw = window.localStorage.getItem(key)
    if (raw == null) return fallback
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return fallback
    return parsed.filter((item): item is string => typeof item === 'string')
  } catch {
    return fallback
  }
}

function writeStringList(key: string, values: string[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(values))
  } catch {
    // Milestone deduplication falls back to memory in browser privacy mode.
  }
}

function showNotification(title: string, options: NotificationOptions) {
  try {
    new Notification(title, options)
    return true
  } catch {
    // Some mobile browsers expose Notification but only allow service-worker notifications.
    return false
  }
}

function writeLocal(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Preference persistence is optional.
  }
}

function initialNotificationStatus(): 'enabled' | 'disabled' | 'blocked' | 'unsupported' {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return 'unsupported'
  if (Notification.permission === 'denied') return 'blocked'
  try {
    return window.localStorage.getItem(notificationPreferenceKey) === 'true' && Notification.permission === 'granted' ? 'enabled' : 'disabled'
  } catch {
    return 'disabled'
  }
}

function notificationButtonLabel(status: 'enabled' | 'disabled' | 'blocked' | 'unsupported') {
  if (status === 'enabled') return '上新提醒已开启'
  if (status === 'blocked') return '通知权限已拒绝'
  if (status === 'unsupported') return '当前不支持通知'
  return '开启上新提醒'
}

function notificationCompactLabel(status: 'enabled' | 'disabled' | 'blocked' | 'unsupported') {
  if (status === 'enabled') return 'ON'
  if (status === 'blocked') return 'BLOCK'
  if (status === 'unsupported') return 'N/A'
  return 'OFF'
}

function formatShanghai(timestamp: number, compact = false) {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return '—'
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    month: compact ? undefined : '2-digit',
    day: compact ? undefined : '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: compact ? undefined : '2-digit',
    hour12: false
  }).format(new Date(timestamp)) + (compact ? '' : ' 上海')
}

function formatOptionalPrice(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '—'
  return formatPrice(value)
}

function formatPrice(value: number) {
  if (value >= 1_000) return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (value >= 10) return value.toFixed(2)
  if (value >= 1) return value.toFixed(3)
  return value.toPrecision(5)
}

function formatCompactPrice(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '—'
  return value >= 1_000 ? `${(value / 1_000).toFixed(1)}K` : formatPrice(value)
}

function formatChange(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`
}

function numberClass(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value) || value === 0) return ''
  return value > 0 ? 'positive' : 'negative'
}

function cleanSymbol(symbol: string) {
  return symbol.replace(/(USDT|USDC|FDUSD)$/i, '')
}

function listingKindClass(kind: RadarListingResult['kind']) {
  if (kind === 'spot_listing') return 'spot'
  if (kind === 'alpha' || kind === 'other') return 'lead'
  return 'launch'
}

function forecastLabel(value: SwingSignal['trendForecast']) {
  if (value === 'bullish') return 'BULLISH'
  if (value === 'range') return 'RANGE'
  if (value === 'defensive') return 'DEFENSIVE'
  return 'UNAVAILABLE'
}

function forecastTone(value: SwingSignal['trendForecast']): RadarTone {
  if (value === 'bullish') return 'positive'
  if (value === 'defensive') return 'negative'
  if (value === 'range') return 'warning'
  return 'neutral'
}

function confidenceLabel(value: SwingSignal['forecastConfidence']) {
  if (value === 'high') return 'HIGH'
  if (value === 'medium') return 'MED'
  if (value === 'low') return 'LOW'
  return '—'
}

function positionLabel(value: SwingSignal['positionTier']) {
  if (value === 'small') return 'SMALL'
  if (value === 'reduce') return 'REDUCE'
  if (value === 'watch') return 'WATCH'
  return 'NONE'
}

function positionTone(value: SwingSignal['positionTier']): RadarTone {
  if (value === 'small') return 'positive'
  if (value === 'reduce') return 'negative'
  if (value === 'watch') return 'warning'
  return 'neutral'
}

function sectionCanBeActionable(status: StrategyRadarSectionStatus | undefined) {
  return status === 'available' || status === 'partial'
}

function marketItemCanBeActionable(item: RadarMarketResult) {
  return item.status === 'available' && !item.isFallback && !item.errorCode
}

function marketSignalForDisplay(market: RadarMarketResult | undefined): SwingSignal {
  if (!market) return unavailableSignalView()
  if (marketItemCanBeActionable(market)) return market.signal
  const reason = market.warning
    || (market.status === 'stale' ? '当前数据源仅返回过期缓存，本轮不生成买卖观察。' : '当前数据源未返回可用行情，本轮不生成买卖观察。')
  return {
    ...market.signal,
    state: 'unavailable',
    label: market.status === 'stale' || market.isFallback ? '缓存待核验' : '行情不可用',
    tone: 'warning',
    outlook: '待核验',
    entryRule: reason,
    exitRule: '恢复为可用、非 fallback 的公开快照后再复核。',
    reasons: [reason, ...market.signal.reasons],
    trendForecast: 'unavailable',
    forecastSummary: '当前数据门禁未通过，不生成未来 1–3 根已闭合 4H 情景。',
    forecastHorizon: 'NEXT_1_TO_3_CLOSED_4H',
    forecastConfidence: 'unavailable',
    positionTier: 'none',
    positionGuidance: '不生成仓位动作，等数据恢复。'
  }
}

function marketStatusDetail(market: RadarMarketResult | undefined, signal: SwingSignal) {
  if (!market) return '聚合行情待核验'
  if (market.warning) return market.warning
  const status = market.status === 'available' ? '可用' : market.status === 'stale' ? '过期' : '不可用'
  const provenance = [market.source, market.cacheStatus ? `cache ${market.cacheStatus}` : ''].filter(Boolean).join(' · ')
  const dataTime = signal.dataTime ? `4H 闭合 · ${formatShanghai(signal.dataTime)}` : '数据时间待核验'
  return `${status}${provenance ? ` · ${provenance}` : ''} · ${dataTime}`
}

function marketDataState(market: RadarMarketResult | undefined) {
  if (!market) return 'WAIT'
  if (marketItemCanBeActionable(market)) return 'OK'
  if (market.status === 'stale') return 'STALE'
  if (market.isFallback) return 'FALLBACK'
  return 'BLOCKED'
}

function marketDataTrace(market: RadarMarketResult | undefined, signal: SwingSignal) {
  const source = market?.source?.includes('PUBLIC_REST') ? 'REST' : market?.source?.replace('BINANCE_', '') || 'NO SOURCE'
  const cache = market?.cacheStatus ? market.cacheStatus.toUpperCase() : 'NO CACHE'
  const dataTime = signal.dataTime ? formatShanghai(signal.dataTime, true) : 'NO TIME'
  return `${source} · ${cache} · ${dataTime}`
}

function sectionError(
  status: StrategyRadarSectionStatus,
  errors: StrategyRadarError[],
  itemCount: number,
  emptyMessage: string
) {
  const detail = [...new Set(errors.map(item => item.message).filter(Boolean))].slice(0, 2).join('；')
  if (status === 'partial') return detail || '仅部分数据可用；另一分区仍按自身状态展示。'
  if (status === 'stale') return detail || '当前展示过期缓存，不生成新的操作观察。'
  if (status === 'unavailable') return detail || '当前数据源无法提供这一分区的数据。'
  if (status === 'demo') return detail || '当前为演示数据，不生成操作观察。'
  if (!itemCount && status !== 'available') return detail || emptyMessage
  return detail
}

function failClosedMarketBatch(previous: RadarMarketBatch | null, message: string): RadarMarketBatch | null {
  if (!previous) return null
  const error: StrategyRadarError = {
    component: 'market',
    subject: null,
    code: 'RADAR_REFRESH_FAILED',
    message,
    retryable: true
  }
  return {
    ...previous,
    status: 'unavailable',
    availableCount: 0,
    errors: [error, ...previous.errors],
    items: previous.items.map(item => ({
      ...item,
      status: 'unavailable',
      warning: `最新刷新失败：${message}`,
      errorCode: 'RADAR_REFRESH_FAILED'
    }))
  }
}

function failClosedListingBatch(previous: RadarListingBatch | null, message: string): RadarListingBatch | null {
  if (!previous) return null
  return {
    ...previous,
    status: 'stale',
    cacheStatus: 'stale',
    errors: [{
      component: 'listing',
      subject: null,
      code: 'RADAR_REFRESH_FAILED',
      message,
      retryable: true
    }, ...previous.errors]
  }
}

function failClosedBStockUniverse(previous: RadarBStockUniverse | null, message: string): RadarBStockUniverse | null {
  if (!previous) return null
  return {
    ...previous,
    status: previous.items.length ? 'partial' : 'unavailable',
    warning: `最新 bStocks 发现刷新失败：${message}`
  }
}

function sectionStatusLabel(status: StrategyRadarSectionStatus | undefined) {
  if (!status) return 'LOADING'
  if (status === 'available') return 'AVAILABLE'
  if (status === 'partial') return 'PARTIAL'
  if (status === 'stale') return 'STALE'
  if (status === 'demo') return 'DEMO'
  return 'UNAVAILABLE'
}

function sectionTone(status: StrategyRadarSectionStatus | undefined): RadarTone {
  if (status === 'available') return 'positive'
  if (status === 'partial' || status === 'stale' || status === 'demo') return 'warning'
  if (status === 'unavailable') return 'negative'
  return 'neutral'
}

function bStockUniverseTone(status: RadarBStockUniverse['status'] | undefined): RadarTone {
  if (status === 'available') return 'positive'
  if (status === 'partial') return 'warning'
  if (status === 'unavailable') return 'negative'
  return 'neutral'
}

function combinedSectionTone(
  marketStatus: StrategyRadarSectionStatus | undefined,
  listingStatus: StrategyRadarSectionStatus | undefined
): RadarTone {
  const tones = [sectionTone(marketStatus), sectionTone(listingStatus)]
  if (tones.every(tone => tone === 'positive')) return 'positive'
  if (tones.includes('negative') && tones.every(tone => tone === 'negative' || tone === 'neutral')) return 'negative'
  if (tones.some(tone => tone === 'negative' || tone === 'warning')) return 'warning'
  return 'neutral'
}

function latestRadarTimestamp(
  metadata: RadarMetadata | null,
  markets: RadarMarketBatch | null,
  listings: RadarListingBatch | null,
  bStocks: RadarBStockUniverse | null
) {
  const values = [metadata?.generatedAt, markets?.fetchedAt, listings?.fetchedAt, bStocks?.fetchedAt]
    .filter((value): value is number => value != null && Number.isFinite(value))
  return values.length ? Math.max(...values) : null
}

function unavailableSignalView(): SwingSignal {
  return {
    state: 'unavailable', label: '数据待核验', tone: 'neutral', outlook: '待核验', price: null,
    ema20: null, ema50: null, rsi14: null, rangeLow: null, rangeHigh: null,
    entryRule: '需要币安公开现货 K 线才生成买入观察。',
    exitRule: '数据不可用时不生成卖出或持有提示。',
    reasons: ['数据待核验'], momentum: [], dataTime: null,
    latestPrice: null, latestPriceAsOf: null,
    trendForecast: 'unavailable',
    forecastSummary: '数据门禁未通过，不预测未来 1–3 根已闭合 4H。',
    forecastHorizon: 'NEXT_1_TO_3_CLOSED_4H',
    forecastConfidence: 'unavailable',
    positionTier: 'none',
    positionGuidance: '不生成仓位动作，等数据恢复。'
  }
}

function errorMessage(error: unknown) {
  if (error instanceof DOMException && error.name === 'AbortError') return '请求已取消'
  return error instanceof Error ? error.message : String(error)
}
